import { useMemo } from 'react'
import { useGetValuation } from 'ClientPortal/src/api/Queries/valuations'
import { throwOnForbiddenApiError } from 'ClientPortal/src/api/react-query-config'
import { useTypedParams } from 'ClientPortal/src/router/useTypedParams'
import type {
    CustomerIncomeTenantParameter as IncomeValuationTenantParameterSet,
    CustomerTenant as TenantV3,
} from '@einwert/valuation-api-client'
import { isEinwertValuation } from 'ClientPortal/src/api/valuationModels'
import { getTenantBreakdown } from '../../AssetDetails.utils'
import { keyBy } from 'lodash'
import { subtractMonetaryValues } from 'ComponentLibrary/src/common/money'
import { subtractAreaValueDataObjects } from 'ComponentLibrary'
import { isParkingType } from 'ComponentLibrary/src/common/tenant'
import { mapEconomicUnitTenantRows } from 'ClientPortal/src/common/tenantDetailsTable'

export function useValuationTenantListPage() {
    const { valuationId } = useTypedParams(['valuationId'])
    const { valuation, isValuationPending } = useGetValuation({ valuationId, throwOnError: throwOnForbiddenApiError })

    const tenantParametersById = useMemo(() => {
        if (!valuation) {
            return {}
        }

        const params = valuation.economic_units[0].income_valuation_parameters.tenant_parameters
        return keyBy(params, 'tenant_uuid')
    }, [valuation])

    const tenantRows = useMemo(() => {
        if (!valuation) {
            return []
        }
        if (isEinwertValuation(valuation)) {
            return valuation.economic_units[0].structures.flatMap((structure, valuationStructureIndex) =>
                mapStructureTenantRows(structure.tenantsV3 ?? [], tenantParametersById, valuationStructureIndex)
            )
        }
        return mapEconomicUnitTenantRows(valuation.economic_units[0].tenantsV3 ?? [], tenantParametersById)
    }, [tenantParametersById, valuation])

    const tenantBreakdown = useMemo(() => {
        return valuation ? getTenantBreakdown(valuation.tenant_summary) : []
    }, [valuation])

    const rentKeyValues = useMemo(() => {
        if (!valuation) {
            return undefined
        }

        const marketRent = valuation.tenant_summary.total?.gross_rental_income.total
        const contractualRent = valuation.tenant_summary.total?.actual_rental_income.total
        const rentDifference = marketRent && contractualRent ? subtractMonetaryValues(contractualRent, marketRent) : undefined
        const rentDifferencePercentage =
            rentDifference && marketRent && marketRent.amount !== 0 ? rentDifference.amount / marketRent.amount : undefined

        return {
            marketRent,
            contractualRent,
            rentDifference,
            rentDifferencePercentage,
        }
    }, [valuation])

    const areaKeyValues = useMemo(() => {
        if (!valuation) {
            return undefined
        }

        const vacancyArea = valuation.tenant_summary.total?.vacancy_area
        const totalArea = valuation.tenant_summary.total?.total_area
        const rentedArea = totalArea && vacancyArea ? subtractAreaValueDataObjects(totalArea, vacancyArea) : undefined
        const rentedPercentage = totalArea && rentedArea && totalArea.value !== 0 ? rentedArea.value / totalArea.value : undefined
        const vacancyPercentage = totalArea && vacancyArea && totalArea.value !== 0 ? vacancyArea.value / totalArea.value : undefined

        return {
            totalArea,
            rentedArea,
            vacancyArea,
            rentedPercentage,
            vacancyPercentage,
        }
    }, [valuation])

    const numberOfUnitsKeyValues = useMemo(() => {
        if (!valuation) {
            return undefined
        }
        const parkingSummaryTenants = tenantBreakdown.filter((row) => row.usage_type && isParkingType(row.usage_type))
        let vacancyUnits = 0
        let totalUnits = 0
        parkingSummaryTenants.forEach((summaryTenant) => {
            if (summaryTenant.vacancy) {
                vacancyUnits += summaryTenant.vacancy?.units
            }
            if (summaryTenant.number_of_units !== undefined) {
                totalUnits += summaryTenant.number_of_units
            }
        })
        const rentedUnits = totalUnits !== undefined && vacancyUnits !== undefined ? totalUnits - vacancyUnits : undefined
        const rentedPercentage = totalUnits && rentedUnits !== undefined && totalUnits !== 0 ? rentedUnits / totalUnits : undefined
        const vacancyPercentage = totalUnits && totalUnits !== 0 ? vacancyUnits / totalUnits : undefined

        return {
            totalUnits,
            rentedUnits,
            vacancyUnits,
            rentedPercentage,
            vacancyPercentage,
        }
    }, [valuation, tenantBreakdown])

    return { isValuationPending, tenantRows, tenantBreakdown, rentKeyValues, areaKeyValues, numberOfUnitsKeyValues }
}

const mapStructureTenantRows = (
    tenants: TenantV3[],
    tenantParameters: Record<string, IncomeValuationTenantParameterSet>,
    valuationStructureIndex: number
) =>
    tenants.map((tenant, tenantIndex) => {
        return {
            ...tenant,
            market_rent: tenantParameters[tenant.uuid]?.reasonable_rent,
            market_rent_details: tenantParameters[tenant.uuid]?.reasonable_rent_details,
            valuationStructureIndex,
            tenantIndex,
        }
    })
